/**
 * TanStack Query hooks for Articles Funnels
 *
 * This file provides React Query hooks for fetching and mutating article funnel data.
 * These hooks replace the async methods in the Zustand articles-funnels store.
 *
 * Note: Most funnel data is populated via the articles queries (getById, getByOrgId, getByBrandId).
 * This file primarily handles funnel-specific mutations.
 *
 * @module articles-funnels-queries
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { type ContentFunnel } from '../../entity/api/content-funnel';
import {
  createArticleFunnel,
  recheckArticleJobsById,
  retryArticleFunnelById,
  saveArticleFunnel,
} from '../../sdk/article-funnel';

// ============ QUERY KEYS ============

export const articlesFunnelsKeys = {
  all: ['articles-funnels'] as const,
  byArticleId: function (articleId: string) {
    return [...articlesFunnelsKeys.all, articleId] as const;
  },
};

// ============ QUERIES ============

/**
 * Get article funnel by article ID from cache
 * Note: Funnel data is typically populated via useArticleById or useArticlesByBrandId queries.
 * This hook reads from cache and doesn't fetch directly.
 *
 * Replaces: getStores().articlesFunnels((s) => s.getByArticleIdFromStore(articleId))
 */
export function useArticleFunnelByArticleId(articleId: string | undefined | null) {
  return useQuery<ContentFunnel | undefined>({
    queryKey: articlesFunnelsKeys.byArticleId(articleId ?? ''),
    queryFn: function (): ContentFunnel | undefined {
      // This query relies on data being populated by useArticleById or useArticlesByBrandId
      // Return undefined if not in cache - the parent article query should populate this
      return undefined;
    },
    enabled: articleId != null && articleId !== '',
    // Cache-only reader: data is populated by useArticleById or useArticlesByBrandId, never refetch
    staleTime: Infinity,
  });
}

// ============ MUTATIONS ============

/**
 * Create a new article funnel
 *
 * Replaces: getStores().articlesFunnels.getState().create(articleId)
 */
export function useCreateArticleFunnel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function (articleId: string) {
      const response = await createArticleFunnel(articleId);
      return { funnel: response.contentFunnel, articleId };
    },
    onSuccess: function ({ funnel, articleId }) {
      // Cache the new funnel
      queryClient.setQueryData(articlesFunnelsKeys.byArticleId(articleId), funnel);
    },
  });
}

/**
 * Save article funnel data
 *
 * Replaces: getStores().articlesFunnels.getState().saveArticleFunnel(articleId, startNextStepGeneration, data)
 */
export function useSaveArticleFunnel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({
      articleId,
      startNextStepGeneration,
      data,
    }: {
      articleId: string;
      startNextStepGeneration: boolean;
      data: Partial<ContentFunnel>;
    }) {
      const response = await saveArticleFunnel(articleId, startNextStepGeneration, data);
      return { funnel: response.data, articleId };
    },
    onSuccess: function ({ funnel, articleId }) {
      // Update the funnel in cache
      queryClient.setQueryData(articlesFunnelsKeys.byArticleId(articleId), funnel);
    },
  });
}

/**
 * Retry article funnel generation
 *
 * Replaces: getStores().articlesFunnels.getState().retryArticleFunnelById(articleId)
 */
export function useRetryArticleFunnel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function (articleId: string) {
      await retryArticleFunnelById(articleId);
      return articleId;
    },
    onSuccess: function (articleId) {
      // Invalidate to refetch when ready
      void queryClient.invalidateQueries({
        queryKey: articlesFunnelsKeys.byArticleId(articleId),
      });
    },
  });
}

/**
 * Recheck article jobs (for funnel status updates)
 *
 * Replaces: getStores().articlesFunnels.getState().recheckArticleJobsById(articleId)
 */
export function useRecheckArticleJobs() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function (articleId: string) {
      await recheckArticleJobsById(articleId);
      return articleId;
    },
    onSuccess: function (articleId) {
      // Invalidate to refetch updated status
      void queryClient.invalidateQueries({
        queryKey: articlesFunnelsKeys.byArticleId(articleId),
      });
    },
  });
}

// ============ CACHE HELPERS ============

/**
 * Get article funnel from cache by article ID
 */
export function getArticleFunnelFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  articleId: string
): ContentFunnel | undefined {
  return queryClient.getQueryData<ContentFunnel>(articlesFunnelsKeys.byArticleId(articleId));
}

/**
 * Set article funnel in cache
 * Also syncs to Zustand for backward compatibility.
 */
export function setArticleFunnelInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  articleId: string,
  funnel: ContentFunnel
): void {
  queryClient.setQueryData(articlesFunnelsKeys.byArticleId(articleId), funnel);
}

/**
 * Remove article funnel from cache by article ID
 * Also syncs to Zustand for backward compatibility.
 */
export function removeArticleFunnelFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  articleId: string
): void {
  queryClient.removeQueries({
    queryKey: articlesFunnelsKeys.byArticleId(articleId),
  });
}

/**
 * Update funnel data in cache (for socket updates, etc.)
 * Merges with existing funnel data
 * Also syncs to Zustand for backward compatibility.
 */
export function updateArticleFunnelInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  articleId: string,
  partialFunnel: Partial<ContentFunnel>
): ContentFunnel | undefined {
  const existingFunnel = getArticleFunnelFromCache(queryClient, articleId);
  if (existingFunnel == null) {
    return undefined;
  }

  const updatedFunnel: ContentFunnel = {
    ...existingFunnel,
    ...partialFunnel,
    updatedAt: Date.now(),
  };

  queryClient.setQueryData(articlesFunnelsKeys.byArticleId(articleId), updatedFunnel);

  return updatedFunnel;
}

/**
 * Invalidate article funnel queries
 */
export function invalidateArticleFunnel(
  queryClient: ReturnType<typeof useQueryClient>,
  articleId: string
): void {
  void queryClient.invalidateQueries({
    queryKey: articlesFunnelsKeys.byArticleId(articleId),
  });
}

/**
 * Invalidate all article funnel queries
 */
export function invalidateAllArticleFunnels(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({
    queryKey: articlesFunnelsKeys.all,
  });
}
